import React, { useState } from "react";
import { Col, message, Modal, notification, Row } from "antd";
import { replaceTktModalConstants } from "../constants";
import Form from "../../../Form";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import PdfIcon from "../../../../assets/images/view_pdf.svg";
import DownloadIcon from "../../../../assets/images/download_icon.svg";
import InputField from "../../../../reusable-components/TNInputField";
import ReplaceTicketDragger from "./ReplaceTicketDragger";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
} from "../../../../enums/ButtonAttributesEnums";
import styles from "./ReplaceTicketModal.module.scss";
import ErrorAlert from "../../../../assets/images/alert-triangle.svg";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import TicketProcessingServices from "../../../../services/ticketProcessing.service";

interface ReplaceTicketModalProps {
  isOpen?: boolean;
  handleClose?: () => void;
  activeTicketUrl: string;
  activeTicketName: string;
  activeTicketId: string;
  handleFormReset: () => void;
  handleInvalidateQueries: () => void;
}

const ReplaceTicketModal = (props: ReplaceTicketModalProps) => {
  const {
    isOpen,
    handleClose,
    activeTicketName,
    activeTicketUrl,
    activeTicketId,
    handleFormReset,
    handleInvalidateQueries,
  } = props;

  const [isReplaceTktError, setIsReplaceTktError] = useState(false);
  const [documentKey, setDocumentKey] = useState(null);

  const { ReplaceTicket } = TicketProcessingServices();
  const { mutateAsync: replaceTicket, isPending: isReplacingTicketPending } =
    ReplaceTicket();

  const removeReplaceTktError = () => setIsReplaceTktError(false);

  const downloadFile = async () => {
    if (!activeTicketUrl) return;

    try {
      const response = await fetch(activeTicketUrl);
      if (!response.ok) throw new Error("File fetch failed");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${activeTicketName}_ticket`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Form>
      {({ getValues, reset, control: { handleSubmit } }) => {
        const handleCancel = () => {
          setIsReplaceTktError(false);
          reset();
          handleClose();
        };

        const handleReplaceTicket = async () => {
          if (!!!documentKey) {
            message.error(replaceTktModalConstants.UPLOAD_DOC_MISSING_ERROR);
            return;
          }

          const payload = {
            id: activeTicketId,
            ticketUploadPicture: documentKey,
          };
          await replaceTicket(payload, {
            onSuccess: () => {
              reset();
              handleClose();
              setDocumentKey(null);
              handleFormReset();
              handleInvalidateQueries();
              notification.success({
                message: replaceTktModalConstants.REPLACE_TKT_SUCCESS,
              });
            },
            onError: () => setIsReplaceTktError(true),
          });
        };

        return (
          <Modal
            visible={isOpen}
            title={replaceTktModalConstants.TITLE}
            okText={replaceTktModalConstants.SUBMIT_BTN_TXT}
            onOk={handleSubmit(() => handleReplaceTicket())}
            onCancel={handleCancel}
            width={replaceTktModalConstants.WIDTH}
            destroyOnClose
          >
            <Row>
              <Col span={24}>
                <>
                  <div className={styles.existingFileHeader}>
                    {replaceTktModalConstants.EXISTING_FILE_LABEL}
                  </div>
                  <div className={styles.existingFileContent}>
                    <div className={styles.existingFileLabel}>
                      <img className={styles.pdfOptionIcon} src={PdfIcon} />
                      <p className={styles.pdfOptionName}>{activeTicketName}</p>
                    </div>
                    <TNButton
                      size={ButtonSize.MEDIUM}
                      shape={ButtonShape.DEFAULT}
                      className={replaceTktModalConstants.DOWNLOAD_BTN_CLASS}
                      onClick={() => downloadFile()}
                      text={
                        <>
                          <img
                            className={styles.downloadIcon}
                            src={DownloadIcon}
                          />
                          <p className={styles.downloadText}>
                            {replaceTktModalConstants.DOWNLOAD}
                          </p>
                        </>
                      }
                    />
                  </div>
                </>
              </Col>
              <Col span={24}>
                <ReplaceTicketDragger
                  onFilesUploaded={() => {}}
                  docType={""}
                  filesUploaded={[]}
                  maxFileCount={1}
                  allowedFileTypes={FileTypes.PDF}
                  setDocumentKey={setDocumentKey}
                  clearReplaceTktError={removeReplaceTktError}
                />
                {isReplaceTktError && (
                  <div className={styles.replaceTktError}>
                    <img src={ErrorAlert} />
                    <div className={styles.singlePageError}>
                      {replaceTktModalConstants.REPLACE_TKT_ERROR}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Modal>
        );
      }}
    </Form>
  );
};

export default ReplaceTicketModal;
